<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchPackages, topupCheckout,
  type GoldPackage, type SpecialOffer, type CustomTerms, type KycTerms, type PaymentMethod,
} from '../game/topup-api';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import packStack from '../assets/omerta/shop/pack-stack.webp';
import packRoll from '../assets/omerta/shop/pack-roll.webp';
import packSatchel from '../assets/omerta/shop/pack-satchel.webp';
import packVault from '../assets/omerta/shop/pack-vault.webp';

const props = defineProps<{ balance?: number; spotlight?: boolean }>();
const emit = defineEmits<{ (e: 'updated'): void }>();

type Tab = 'buy' | 'deals';
const tab = ref<Tab>('buy');

const packages = ref<GoldPackage[]>([]);
const offers = ref<SpecialOffer[]>([]);
const custom = ref<CustomTerms | null>(null);
const kyc = ref<KycTerms | null>(null);
const paymentMethods = ref<PaymentMethod[]>([]);
const selectedMethod = ref<PaymentMethod | null>(null);

/** The credited-bars row for a gold-buy confirm — no commission, 1:1 pay-in. */
function breakdownRows(bars: number) {
  return [
    { label: 'Credited to account', value: bars.toLocaleString(), icon: goldBarsIcon, tone: 'success' as const },
  ];
}
const COMMISSION_NOTE = 'Real-money purchase — test mode, no actual charge.';
// KYC gate active for this player: big spends will be refused until approved.
const kycGate = computed(() =>
  !!kyc.value && kyc.value.enabled && kyc.value.status !== 'APPROVED');
const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const justGranted = ref<number | null>(null); // bars from the last inline mock settle

// ── Custom amount ──
const customBars = ref(1000);
const customCents = computed(() =>
  custom.value ? Math.max(1, Math.round(customBars.value * custom.value.centsPerBar)) : 0);
const customValid = computed(() =>
  !!custom.value && Number.isInteger(customBars.value)
  && customBars.value >= custom.value.minBars && customBars.value <= custom.value.maxBars);

// ── Deal countdown to the next 8-hour rotation — the "limited time" pressure ──
const ROTATION_MS = 8 * 60 * 60 * 1000;
const offersEndAt = ref<number | null>(null);
const countdown = ref('');
let countdownTimer = 0;
function tickCountdown() {
  const now = Date.now();
  // Server sends the window end; the epoch-based fallback lands on the same boundary.
  const end = offersEndAt.value ?? (Math.floor(now / ROTATION_MS) + 1) * ROTATION_MS;
  const diff = Math.max(0, end - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  countdown.value = `${pad(h)}:${pad(m)}:${pad(s)}`;
  // The window rolled over while the tab was open — pull the fresh slate.
  if (diff === 0 && !refreshingOffers) void refreshOffers();
}

let refreshingOffers = false;
async function refreshOffers() {
  refreshingOffers = true;
  try {
    const v = await fetchPackages();
    offers.value = v.offers;
    offersEndAt.value = v.offersEndAt;
  } catch { /* keep the stale slate; next tick retries */ }
  finally { refreshingOffers = false; }
}

onMounted(async () => {
  tickCountdown();
  countdownTimer = window.setInterval(tickCountdown, 1000);
  try {
    const v = await fetchPackages();
    packages.value = v.packages;
    offers.value = v.offers;
    offersEndAt.value = v.offersEndAt ?? null;
    custom.value = v.custom;
    kyc.value = v.kyc ?? null;
    paymentMethods.value = v.paymentMethods ?? [];
    if (v.paymentMethods?.length) selectedMethod.value = v.paymentMethods[0];
    customBars.value = Math.max(v.custom.minBars, 1000);
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
});
onUnmounted(() => { if (countdownTimer) window.clearInterval(countdownTimer); });

function openTab(t: Tab) { tab.value = t; }

function price(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

/** Per-tier dressing — ported 1:1 from dominion's PACKS. */
interface TierLook {
  art: string; color: string;
  ribbon?: string; ribbonIcon?: string;
  card?: 'popular' | 'whale';
  btn: 'grey' | 'gold' | 'purple' | 'bright';
}
const TIERS: Record<string, TierLook> = {
  stack:   { art: packStack,   color: '#c8a24c', btn: 'grey' },
  roll:    { art: packRoll,    color: '#e3c878', btn: 'gold', ribbon: 'Popular', ribbonIcon: '★', card: 'popular' },
  satchel: { art: packSatchel, color: '#9a86ab', btn: 'purple' },
  vault:   { art: packVault,   color: '#e3c878', btn: 'bright', ribbon: 'Best value', ribbonIcon: '♛', card: 'whale' },
};
function tier(p: GoldPackage, idx: number): TierLook {
  return TIERS[p.id] ?? Object.values(TIERS)[Math.min(idx, 3)];
}

/** Deal cards borrow the pack renders, tiered by bar size, so offers look like
 *  premium bundles (art + a gold-family accent) instead of flat coloured chips. */
function dealTier(bars: number): { art: string; color: string } {
  if (bars < 1000)  return { art: packStack,   color: '#c8a24c' };
  if (bars < 3000)  return { art: packRoll,    color: '#dcb85e' };
  if (bars < 10000) return { art: packSatchel, color: '#e3c878' };
  return { art: packVault, color: '#f0dda4' };
}

const redirecting = ref(false);

async function settle(res: { completed: boolean; url: string; bars: number }) {
  if (res.completed) {
    justGranted.value = res.bars;
    toast(`+${res.bars.toLocaleString()} gold bars — the vault thanks you`, { icon: goldBarsIcon });
    emit('updated');
  } else {
    redirecting.value = true;
    window.location.href = res.url;
  }
}

async function buy(p: GoldPackage) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: 'The Gold Reserve',
    title: `Buy ${p.name}?`,
    icon: tier(p, 0).art,
    rows: [
      ...breakdownRows(p.bars),
      { label: 'Total', value: price(p.priceCents), tone: 'bright' },
    ],
    note: COMMISSION_NOTE,
    confirmLabel: `Pay ${price(p.priceCents)}`,
  });
  if (!ok) return;
  busy.value = p.id; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ packageId: p.id, paymentMethod: selectedMethod.value! })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function buyCustom() {
  if (busy.value || !customValid.value) return;
  const ok = await confirmAction({
    kicker: 'The Gold Reserve',
    title: `Buy ${customBars.value.toLocaleString()} gold bars?`,
    icon: goldBarsIcon,
    rows: [
      ...breakdownRows(customBars.value),
      { label: 'Total', value: price(customCents.value), tone: 'bright' },
    ],
    note: COMMISSION_NOTE,
    confirmLabel: `Pay ${price(customCents.value)}`,
  });
  if (!ok) return;
  busy.value = 'custom'; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ bars: customBars.value, paymentMethod: selectedMethod.value! })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function buyOffer(o: SpecialOffer) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: 'Special offer',
    title: `Grab ${o.name}?`,
    icon: goldBarsIcon,
    rows: [
      ...breakdownRows(o.bars),
      { label: 'Was', value: price(o.originalCents), tone: 'dim' },
      { label: `Total −${o.savings}%`, value: price(o.priceCents), tone: 'bright' },
    ],
    note: COMMISSION_NOTE,
    confirmLabel: `Pay ${price(o.priceCents)}`,
  });
  if (!ok) return;
  busy.value = o.id; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ offerId: o.id, paymentMethod: selectedMethod.value! })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}
</script>

<template>
  <section class="relative">
    <!-- Redirect overlay — shown while the browser navigates to the payment provider -->
    <div v-if="redirecting" class="absolute inset-0 z-50 grid place-items-center bg-bg/90 backdrop-blur-sm">
      <div class="text-center">
        <div class="gr-spinner mb-3"></div>
        <p class="font-heading text-lg text-gold">Redirecting to payment&hellip;</p>
        <p class="font-body text-sm text-text-dim mt-1">You will be taken to the secure checkout.</p>
      </div>
    </div>

    <!-- header: kicker + vault title, balance pill on the right (dominion ShopPage) -->
    <div class="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-border/70">
      <div>
        <div class="font-label uppercase tracking-[0.3em] text-[9px] text-text-dim">Premium reserve</div>
        <h1 class="flex items-center gap-3 font-heading font-black text-[28px] text-[#f0e6d0] tracking-[0.05em] leading-tight">
          <img :src="goldBarsIcon" class="w-7 h-7 gr-gem" alt="" />
          The Gold Reserve
        </h1>
      </div>
      <div class="gr-balance" v-if="props.balance != null">
        <img :src="goldBarsIcon" class="w-4 h-4" alt="" />
        <div>
          <div class="font-heading text-[8px] tracking-[0.2em] uppercase text-text-dim leading-none">Balance</div>
          <div class="font-heading font-black text-lg text-gold leading-tight">{{ props.balance.toLocaleString() }}</div>
        </div>
      </div>
    </div>

    <!-- tab rail (dominion's buy/history) -->
    <nav class="flex flex-wrap items-center gap-1 border-b border-border/60 mb-6">
      <!-- rail scrolls sideways on mobile (hdr-rail hides the scrollbar) -->
      <button :class="['tab-deco shrink-0 whitespace-nowrap', tab === 'buy' && 'tab-deco-active']" @click="openTab('buy')">Buy bars</button>
      <button :class="['tab-deco shrink-0 whitespace-nowrap', tab === 'deals' && 'tab-deco-active']" @click="openTab('deals')">Special offers</button>
    </nav>

    <p v-if="loading" class="text-text-dim">Loading&hellip;</p>
    <p v-if="error" class="mb-3 text-sm text-error">{{ error }}</p>

    <!-- KYC purchase gate (dominion parity): warn before the server refuses -->
    <div v-if="kycGate && (tab === 'buy' || tab === 'deals')" class="gr-kyc">
      <span class="gr-kyc-badge">ID</span>
      <span>
        The house checks papers on big spends — purchases of
        <strong>{{ price(kyc!.thresholdCents) }}</strong> or more require identity verification
        <template v-if="kyc!.status === 'PENDING'">(yours is under review)</template>.
      </span>
    </div>

    <!-- ── Payment method selector ── -->
    <div v-if="paymentMethods.length > 1 && !loading" class="gr-methods">
      <div class="gr-methods-label">Pay with</div>
      <div class="gr-methods-row">
        <button
          v-for="m in paymentMethods" :key="m"
          :class="['gr-method', selectedMethod === m && 'gr-method--active']"
          @click="selectedMethod = m"
        >{{ m }}</button>
      </div>
    </div>

    <!-- ── BUY TAB ── -->
    <template v-if="tab === 'buy' && !loading">
      <!-- inline settle banner (mock) -->
      <div v-if="justGranted !== null" class="gr-granted">
        <img :src="goldBarsIcon" class="w-6 h-6 gr-gem" alt="" />
        <span><strong class="text-gold-bright">+{{ justGranted.toLocaleString() }}</strong> gold bars added to your account.</span>
      </div>

      <div class="gr-grid">
        <div
          v-for="(p, idx) in packages" :key="p.id"
          class="gr-pack"
          :class="{ 'gr-popular': tier(p, idx).card === 'popular', 'gr-whale': tier(p, idx).card === 'whale', 'guide-glow': props.spotlight }"
          :style="{ '--gr-c': tier(p, idx).color }"
        >
          <div class="gr-wash" aria-hidden="true"></div>
          <div v-if="tier(p, idx).ribbon" class="gr-ribbon">
            <span class="gr-ribbon-ic">{{ tier(p, idx).ribbonIcon }}</span>{{ tier(p, idx).ribbon }}
          </div>
          <div class="gr-art"><img :src="tier(p, idx).art" :alt="p.name" /></div>
          <div class="gr-amount" :style="{ color: tier(p, idx).color, textShadow: `0 0 16px ${tier(p, idx).color}55` }">
            {{ p.bars.toLocaleString() }}
          </div>
          <div class="gr-unit">Gold bars</div>
          <div class="gr-price">{{ price(p.priceCents) }}</div>
          <button
            class="gr-buy" :class="`gr-buy--${tier(p, idx).btn}`"
            :disabled="busy !== null"
            @click="buy(p)"
          >{{ busy === p.id ? 'Processing…' : 'Buy now' }}</button>
        </div>
      </div>

      <!-- custom amount — buy any number of bars at the base rate -->
      <div v-if="custom" class="gr-custom">
        <div class="gr-custom-head">
          <div class="font-heading text-lg text-gold">Name your own amount</div>
          <div class="font-body text-xs text-text-dim">Any amount from {{ custom.minBars.toLocaleString() }} to {{ custom.maxBars.toLocaleString() }} bars — base rate, no bundle bonus.</div>
        </div>
        <div class="gr-custom-body">
          <input
            type="range" class="gr-slider"
            :min="custom.minBars" :max="custom.maxBars" step="50"
            v-model.number="customBars"
          />
          <div class="gr-custom-input">
            <img :src="goldBarsIcon" class="w-4 h-4 shrink-0" alt="" />
            <input
              type="number" class="gr-custom-num"
              :min="custom.minBars" :max="custom.maxBars" v-model.number="customBars"
            />
            <span class="font-label uppercase tracking-wider text-[10px] text-text-dim">bars</span>
          </div>
          <div class="gr-custom-price">{{ price(customCents) }}</div>
          <button
            class="gr-buy gr-buy--bright gr-custom-buy"
            :disabled="busy !== null || !customValid"
            @click="buyCustom"
          >{{ busy === 'custom' ? 'Processing…' : 'Buy custom' }}</button>
        </div>
        <p v-if="!customValid" class="font-body text-[11px] text-error mt-1">
          Enter a whole number between {{ custom.minBars.toLocaleString() }} and {{ custom.maxBars.toLocaleString() }}.
        </p>
      </div>

      <p class="mt-5 text-center font-body italic text-[11px] text-text-dim/70 leading-snug">
        Every buy is credited in full — no commission. Test mode — no real charge is made.
      </p>
    </template>

    <!-- ── SPECIAL OFFERS TAB (discounted limited-time bundles) ── -->
    <template v-else-if="tab === 'deals'">
      <div v-if="justGranted !== null" class="gr-granted">
        <img :src="goldBarsIcon" class="w-6 h-6 gr-gem" alt="" />
        <span><strong class="text-gold-bright">+{{ justGranted.toLocaleString() }}</strong> gold bars added to your account.</span>
      </div>

      <div class="gr-deals-head">
        <div>
          <div class="font-heading text-lg text-gold">Today's deals</div>
          <div class="font-body text-xs text-text-dim">More bars per euro — a fresh slate every 8 hours.</div>
        </div>
        <div class="gr-countdown" aria-label="Time left">
          <span class="gr-cd-label">Ends in</span>
          <span class="gr-cd-clock">{{ countdown }}</span>
        </div>
      </div>

      <div class="gr-deal-grid">
        <div v-for="o in offers" :key="o.id" class="gr-deal" :style="{ '--gr-c': dealTier(o.bars).color }">
          <div class="gr-wash" aria-hidden="true"></div>
          <span class="gr-deal-tag">{{ o.tag }}</span>
          <span class="gr-deal-save">−{{ o.savings }}%</span>
          <div class="gr-deal-art"><img :src="dealTier(o.bars).art" :alt="o.name" /></div>
          <div class="gr-deal-bars">
            <img :src="goldBarsIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
            <b :style="{ color: dealTier(o.bars).color }">{{ o.bars.toLocaleString() }}</b>
          </div>
          <div class="gr-deal-name">{{ o.name }}</div>
          <div class="gr-deal-price">
            <s>{{ price(o.originalCents) }}</s>
            <b>{{ price(o.priceCents) }}</b>
          </div>
          <button
            class="gr-buy gr-buy--gold gr-deal-buy"
            :disabled="busy !== null"
            @click="buyOffer(o)"
          >{{ busy === o.id ? 'Processing…' : 'Grab it' }}</button>
        </div>
      </div>

      <p class="mt-5 text-center font-body italic text-[11px] text-text-dim/70 leading-snug">
        Test mode — no real charge is made.
      </p>
    </template>

    <!-- Purchases, invoices, and the full transaction ledger now live in
         The Back Office (Settings → Billing) — this shop is for buying only. -->
    <p v-if="tab === 'buy' && !loading" class="mt-2 text-center font-body text-[11px] text-text-dim/60">
      Looking for receipts? Purchases, invoices &amp; transactions moved to
      <span class="text-gold/80">Settings → Billing</span>.
    </p>
  </section>
</template>

<style scoped>
.gr-gem { animation: gr-gem-pulse 2.6s ease-in-out infinite; }
@keyframes gr-gem-pulse {
  0%, 100% { filter: drop-shadow(0 0 2px rgba(227, 200, 120, 0.25)); transform: scale(1); }
  50% { filter: drop-shadow(0 0 9px rgba(227, 200, 120, 0.75)); transform: scale(1.06); }
}

.gr-balance {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px;
  background: rgba(200, 162, 76, 0.05);
  border: 1px solid rgba(200, 162, 76, 0.18);
}

.gr-granted {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-bottom: 16px;
  padding: 10px 16px;
  border: 1px solid rgba(200, 162, 76, 0.35);
  border-left: 3px solid var(--color-gold);
  background: rgba(200, 162, 76, 0.07);
  font-family: var(--font-body, serif);
  font-size: 15px;
  color: var(--color-text);
}

/* KYC gate notice — amber ledger-stamp banner */
.gr-kyc {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 16px;
  padding: 10px 16px;
  border: 1px solid rgba(214, 158, 46, 0.35);
  border-left: 3px solid #d69e2e;
  background: rgba(214, 158, 46, 0.07);
  font-family: var(--font-body, serif);
  font-size: 14px;
  color: var(--color-text);
}
.gr-kyc-badge {
  flex: none;
  padding: 3px 7px;
  border: 1px solid #d69e2e;
  border-radius: 2px;
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.18em;
  color: #d69e2e;
}

.gr-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
@media (max-width: 900px) { .gr-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 460px) { .gr-grid { grid-template-columns: 1fr; } }

.gr-pack {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  /* content-sized: the old min-height 250px stretched a dead band between the
     art and the amount on every card (whitespace pass, player feedback) */
  padding: 14px 12px 12px;
  background: linear-gradient(160deg, #14101c, #0a0812);
  border: 1px solid var(--color-border);
  overflow: hidden;
  transition: transform 200ms ease, box-shadow 200ms ease;
}
.gr-pack:hover { transform: translateY(-4px); box-shadow: 0 8px 28px rgba(0, 0, 0, 0.8); }
.gr-popular { border-color: rgba(227, 200, 120, 0.35); box-shadow: 0 0 16px rgba(227, 200, 120, 0.06); }
.gr-popular:hover { box-shadow: 0 12px 40px rgba(227, 200, 120, 0.14); }
.gr-whale { border-color: rgba(200, 162, 76, 0.4); box-shadow: 0 0 14px rgba(200, 162, 76, 0.08); }
.gr-whale:hover { box-shadow: 0 12px 40px rgba(200, 162, 76, 0.16); }

.gr-wash {
  position: absolute; inset: 0;
  pointer-events: none;
  opacity: 0.07;
  background: radial-gradient(ellipse at 50% 0%, var(--gr-c), transparent 70%);
}
.gr-ribbon {
  position: absolute; top: -1px; left: -1px;
  z-index: 2;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  font-family: var(--font-heading, serif);
  font-size: 8px; font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase;
  background: var(--color-gold);
  color: #06040e;
  white-space: nowrap;
}
.gr-ribbon-ic { font-size: 9px; line-height: 1; }

.gr-art {
  width: 88px; height: 88px;
  margin-top: 8px;
  position: relative; z-index: 1;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.6));
}
.gr-art img { width: 100%; height: 100%; object-fit: contain; }

.gr-amount {
  position: relative; z-index: 1;
  margin-top: 9px;
  font-family: var(--font-heading, serif);
  font-size: 27px; font-weight: 700; line-height: 1;
  font-variant-numeric: tabular-nums;
}
.gr-unit {
  position: relative; z-index: 1;
  margin-top: 3px;
  font-family: var(--font-heading, serif);
  font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase;
  color: #8a7a60;
}
.gr-price {
  position: relative; z-index: 1;
  margin-top: 6px;
  margin-bottom: 10px;
  font-family: var(--font-heading, serif);
  font-size: 16px; font-weight: 900;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.gr-buy {
  position: relative; z-index: 1;
  margin-top: auto;
  width: 100%;
  padding: 8px 0;
  font-family: var(--font-heading, serif);
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  border: 1px solid;
  clip-path: polygon(4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px),
                     calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px), 0 4px);
  cursor: pointer;
  transition: filter 150ms ease, background 150ms ease;
}
.gr-buy:hover:not(:disabled) { filter: brightness(1.25); }
.gr-buy:active:not(:disabled) { transform: translateY(1px); }
.gr-buy:disabled { opacity: 0.55; cursor: wait; }
.gr-buy--grey   { background: linear-gradient(180deg, #1a1622, #100d16); border-color: #2f2a3c; color: #9a8f7a; }
.gr-buy--gold   { background: linear-gradient(180deg, #2a1f08, #18120a); border-color: rgba(201, 145, 58, 0.5); color: #e3c878; }
.gr-buy--purple { background: linear-gradient(180deg, #1b1524, #110d18); border-color: rgba(154, 134, 171, 0.45); color: #9a86ab; }
.gr-buy--bright { background: linear-gradient(180deg, #2e2409, #1a1408); border-color: rgba(227, 200, 120, 0.6); color: #f0dda4; }

/* ── custom amount panel ── */
.gr-custom {
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(200, 162, 76, 0.28);
  background: radial-gradient(120% 100% at 50% 0%, rgba(200, 162, 76, 0.06), transparent 70%), rgba(0, 0, 0, 0.18);
}
.gr-custom-head { margin-bottom: 9px; }
.gr-custom-body { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.gr-slider { flex: 1 1 240px; min-width: 180px; accent-color: var(--color-gold); cursor: pointer; }
.gr-custom-input {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 10px; border: 1px solid var(--color-border); background: rgba(0, 0, 0, 0.3);
}
.gr-custom-num {
  width: 84px; background: transparent; border: 0; outline: none;
  font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
}
.gr-custom-num::-webkit-outer-spin-button, .gr-custom-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.gr-custom-price {
  font-family: var(--font-heading, serif); font-size: 18px; font-weight: 900; color: #fff;
  font-variant-numeric: tabular-nums; min-width: 72px; text-align: right;
}
.gr-custom-buy { width: auto; min-width: 130px; margin-top: 0; padding: 9px 18px; }

/* ── special offers ── */
.gr-deals-head {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-bottom: 16px; flex-wrap: wrap;
}
.gr-countdown {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px; border: 1px solid rgba(179, 65, 62, 0.45);
  background: rgba(179, 65, 62, 0.08);
}
.gr-cd-label { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--color-text-dim); }
.gr-cd-clock { font-family: var(--font-heading, serif); font-size: 17px; font-variant-numeric: tabular-nums; color: #e6a24a; letter-spacing: 0.04em; }

.gr-deal-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
}
@media (max-width: 1100px) { .gr-deal-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 720px) { .gr-deal-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 400px) { .gr-deal-grid { grid-template-columns: 1fr; } }

.gr-deal {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  padding: 28px 12px 12px;
  background: linear-gradient(160deg, #14101c, #0a0812);
  border: 1px solid color-mix(in srgb, var(--gr-c) 38%, var(--color-border));
  overflow: hidden;
  transition: transform 160ms ease, box-shadow 160ms ease;
}
.gr-deal:hover { transform: translateY(-3px); box-shadow: 0 10px 26px rgba(0, 0, 0, 0.7); }
.gr-deal-tag {
  position: absolute; top: 0; left: 0; z-index: 2;
  padding: 3px 9px;
  font-family: var(--font-label, sans-serif); font-size: 8px; font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase;
  background: var(--gr-c); color: #06040e;
}
.gr-deal-save {
  position: absolute; top: 0; right: 0; z-index: 2;
  padding: 3px 8px;
  font-family: var(--font-heading, serif); font-size: 12px; font-weight: 900;
  color: var(--color-success);
  background: rgba(0, 0, 0, 0.45);
  border-left: 1px solid color-mix(in srgb, var(--color-success) 40%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--color-success) 40%, transparent);
}
.gr-deal-art {
  width: 68px; height: 68px;
  position: relative; z-index: 1;
  filter: drop-shadow(0 5px 12px rgba(0, 0, 0, 0.6));
}
.gr-deal-art img { width: 100%; height: 100%; object-fit: contain; }
.gr-deal-bars {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 10px;
  position: relative; z-index: 1;
  font-family: var(--font-heading, serif);
}
.gr-deal-bars b {
  font-size: 22px; font-weight: 700; line-height: 1;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 14px color-mix(in srgb, var(--gr-c) 40%, transparent);
}
.gr-deal-name {
  margin-top: 5px; text-align: center;
  position: relative; z-index: 1;
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em;
  text-transform: uppercase; color: #8a7a60; min-height: 24px;
}
.gr-deal-price {
  display: flex; align-items: baseline; gap: 8px; margin-top: 6px; margin-bottom: 12px;
  position: relative; z-index: 1;
  font-variant-numeric: tabular-nums;
}
.gr-deal-price s { font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim); }
.gr-deal-price b { font-family: var(--font-heading, serif); font-size: 17px; font-weight: 900; color: #fff; }
.gr-deal-buy { width: 100%; margin-top: auto; position: relative; z-index: 1; }

.gr-spinner {
  width: 32px; height: 32px; margin: 0 auto;
  border: 3px solid rgba(200, 162, 76, 0.2);
  border-top-color: var(--color-gold);
  border-radius: 50%;
  animation: gr-spin 0.8s linear infinite;
}
@keyframes gr-spin { to { transform: rotate(360deg); } }

/* ── payment method selector ── */
.gr-methods {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid rgba(200, 162, 76, 0.18);
  background: rgba(200, 162, 76, 0.04);
}
.gr-methods-label {
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.gr-methods-row { display: flex; gap: 8px; }
.gr-method {
  padding: 7px 18px;
  font-family: var(--font-heading, serif);
  font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid var(--color-border);
  background: rgba(0, 0, 0, 0.3);
  color: var(--color-text-dim);
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, background 150ms ease;
}
.gr-method:hover { border-color: rgba(200, 162, 76, 0.4); color: var(--color-text); }
.gr-method--active {
  border-color: var(--color-gold);
  background: rgba(200, 162, 76, 0.12);
  color: var(--color-gold);
  box-shadow: 0 0 10px rgba(200, 162, 76, 0.15);
}

@media (prefers-reduced-motion: reduce) {
  .gr-gem { animation: none; }
  .gr-spinner { animation: none; border-top-color: var(--color-gold); }
  .gr-pack, .gr-pack:hover { transition: none; transform: none; }
}
</style>
